import { useEffect, useMemo } from 'react';
import {
  Navigate,
  Outlet,
  RouterProvider,
  createBrowserRouter,
  createMemoryRouter,
  useLocation,
} from 'react-router-dom';

import { PageTitle } from '@/app/components/page-title';
import PlatformSecondSidebarLayout from '@/app/components/platform-second-sidebar-layout';
import ProjectSettingsLayout from '@/app/components/project-settings-layout';
import { ChatPage } from '@/app/routes/chat';
import { INTERNAL_ADMIN_PATH } from '@/lib/internal-admin-area';
import { EmbedPage } from '@/app/routes/embed';
import { KizeoEmbedPage } from '@/app/routes/embed/kizeo';
import AnalyticsPage from '@/app/routes/platform/analytics';
import { ApiKeysPage } from '@/app/routes/platform/security/api-keys';
import { SigningKeysPage } from '@/app/routes/platform/security/signing-keys';
import { SSOPage } from '@/app/routes/platform/security/sso';
import AIProvidersPage from '@/app/routes/platform/setup/ai';
import { BrandingPage } from '@/app/routes/platform/setup/branding';
import { PlatformPiecesPage } from '@/app/routes/platform/setup/pieces';
import { RedirectPage } from '@/app/routes/redirect';
import { FlowRunsPage } from '@/app/routes/runs';
import { ProjectPiecesPage } from '@/app/routes/settings/pieces';
import { useEmbedding } from '@/components/embed-provider';
import { VerifyEmail } from '@/features/authentication/components/verify-email';
import { AcceptInvitation } from '@/features/team/component/accept-invitation';
import { ApFlagId, Permission } from '@activepieces/shared';
import {
  ActivepiecesClientEventName,
  ActivepiecesVendorEventName,
  ActivepiecesVendorRouteChanged,
} from 'ee-embed-sdk';

import { AdminContainer } from '../components/internal-admin/admin-container';
import { AdminLandingRedirect } from '../components/internal-admin/admin-landing-redirect';
import { AdminSecondSidebarLayout } from '../components/internal-admin/admin-second-sidebar-layout';
import { AllowOnlyLoggedInUserOnlyGuard } from '../components/allow-logged-in-user-only-guard';
import { KizeoAdminAuthGuard } from '../components/internal-admin/kizeo-admin-auth-guard';
import { DashboardContainer } from '../components/dashboard-container';
import { PlatformAdminContainer } from '../components/platform-admin-container';
import AdminClientsPage from '../routes/internal-admin/clients';
import ClientDetailPage from '../routes/internal-admin/clients/client-detail';
import ClientExecutionsTab from '../routes/internal-admin/clients/tabs/client-executions-tab';
import ClientFlowsTab from '../routes/internal-admin/clients/tabs/client-flows-tab';
import ClientMcpsTab from '../routes/internal-admin/clients/tabs/client-mcps-tab';
import ClientUsersTab from '../routes/internal-admin/clients/tabs/client-users-tab';
import AdminHealthPage from '../routes/internal-admin/health';
import AdminExecutionsPage from '../routes/internal-admin/executions';
import AdminFairQueuePage from '../routes/internal-admin/fair-queue';
import AdminFlowsPage from '../routes/internal-admin/flows';
import AdminExecutionViewerPage from '../routes/internal-admin/executions/execution-detail';
import AdminFlowViewerPage from '../routes/internal-admin/flows/flow-detail';
import AdminMcpsPage from '../routes/internal-admin/mcps';
import AdminHomePage from '../routes/internal-admin/home';
import AdminCatalogPage from '../routes/internal-admin/catalog';
import AdminCatalogPieceDetailPage from '../routes/internal-admin/catalog/piece-detail';
import AdminSwaggersPage from '../routes/internal-admin/swaggers';
import AdminSwaggerDetailPage from '../routes/internal-admin/swaggers/swagger-detail';
import AdminMcpDetailPage from '../routes/internal-admin/mcps/mcp-detail';
import AdminUsersPage from '../routes/internal-admin/users';
import UserDetailPage from '../routes/internal-admin/users/user-detail';
import UserFlowsTab from '../routes/internal-admin/users/tabs/user-flows-tab';
import UserExecutionsTab from '../routes/internal-admin/users/tabs/user-executions-tab';
import UserMcpsTab from '../routes/internal-admin/users/tabs/user-mcps-tab';
import NotFoundPage from '../routes/404-page';
import AuthenticatePage from '../routes/authenticate';
import { ChangePasswordPage } from '../routes/change-password';
import { AppConnectionsPage } from '../routes/connections';
import { EmbeddedConnectionDialog } from '../routes/embed/embedded-connection-dialog';
import { FlowsPage } from '../routes/flows';
import { FlowBuilderPage } from '../routes/flows/id';
import { ResetPasswordPage } from '../routes/forget-password';
import { FormPage } from '../routes/forms';
import McpServersPage from '../routes/mcp-servers';
import McpPage from '../routes/mcp-servers/id';
import IssuesPage from '../routes/issues';
import PlansPage from '../routes/plans';
import SettingsBilling from '../routes/platform/billing';
import SettingsHealthPage from '../routes/platform/infra/health';
import SettingsWorkersPage from '../routes/platform/infra/workers';
import { PlatformMessages } from '../routes/platform/notifications/platform-messages';
import ProjectsPage from '../routes/platform/projects';
import AuditLogsPage from '../routes/platform/security/audit-logs';
import { ProjectRolePage } from '../routes/platform/security/project-role';
import { GlobalConnectionsTable } from '../routes/platform/setup/connections';
import { LicenseKeyPage } from '../routes/platform/setup/license-key';
import TemplatesPage from '../routes/platform/setup/templates';
import UsersPage from '../routes/platform/users';
import { ProjectReleasesPage } from '../routes/project-release';
import ViewRelease from '../routes/project-release/view-release';
import { FlowRunPage } from '../routes/runs/id';
import AlertsPage from '../routes/settings/alerts';
import AppearancePage from '../routes/settings/appearance';
import { EnvironmentPage } from '../routes/settings/environment';
import GeneralPage from '../routes/settings/general';
import SessionsPage from '../routes/settings/sessions';
import TeamPage from '../routes/settings/team';
import { SignInPage } from '../routes/sign-in';
import { SignUpPage } from '../routes/sign-up';
import { ShareTemplatePage } from '../routes/templates/share-template';

import { BlockingPiecesModal } from '../../features/flows/components/blocking-pieces-modal';
import { AfterImportFlowRedirect } from './after-import-flow-redirect';
import { DefaultRoute } from './default-route';
import { EmbedRedirectGuard } from './embed-redirect-guard';
import { FlagRouteGuard } from './flag-route-guard';
import { McpAccessGuard } from './mcp-access-guard';
import { RoutePermissionGuard } from './permission-guard';
import { ProjectRouterWrapper } from './project-route-wrapper';
import { ApTableEditorPage } from '../routes/ap-tables/id';
import { ApTableStateProvider } from '@/features/tables/components/ap-table-state-provider';
import { ApTablesPage } from '../routes/ap-tables';

// Blocked routes (temporary until collaboration system is implemented)
const BLOCKED_STATIC_ROUTES = new Set([
  '/forget-password',
  '/reset-password',
  '/verify-email',
  '/team',
  '/invitation',
]);

const SETTINGS_ALLOWED = new Set([
  '/settings',
  '/settings/general',
  '/settings/appearance',
  '/settings/sessions',
]);

const isRouteBlocked = (path: string): boolean => {
  const normalized = path.replace(/^\/projects\/:projectId/, '');
  if (BLOCKED_STATIC_ROUTES.has(normalized)) return true;
  if (normalized.startsWith('/releases')) return true;
  if (normalized.startsWith('/platform')) return true;
  if (normalized.startsWith('/settings')) {
    return !SETTINGS_ALLOWED.has(normalized);
  }
  return false;
};

const SettingsRerouter = () => {
  const { hash } = useLocation();
  const fragmentWithoutHash = hash.slice(1).toLowerCase();
  return fragmentWithoutHash ? (
    <Navigate to={`/settings/${fragmentWithoutHash}`} replace />
  ) : (
    <Navigate to="/settings/general" replace />
  );
};

const routes = [
  {
    path: '/embed',
    element: <EmbedPage></EmbedPage>,
  },
  {
    path: '/embed/kizeo',
    element: <KizeoEmbedPage />,
  },
  {
    path: '/embed/connections',
    element: <EmbeddedConnectionDialog></EmbeddedConnectionDialog>,
  },
  {
    path: '/authenticate',
    element: <AuthenticatePage />,
  },
  ...ProjectRouterWrapper({
    path: '/flows',
    element: (
      <>
        <DashboardContainer>
          <RoutePermissionGuard permission={Permission.READ_FLOW}>
            <PageTitle title="Flows">
              <FlowsPage />
            </PageTitle>
          </RoutePermissionGuard>
        </DashboardContainer>
        <BlockingPiecesModal />
      </>
    ),
  }),
  ...ProjectRouterWrapper({
    path: '/flows/:flowId',
    element: (
      <>
        <AllowOnlyLoggedInUserOnlyGuard>
          <RoutePermissionGuard permission={Permission.READ_FLOW}>
            <PageTitle title="Builder">
              <FlowBuilderPage />
            </PageTitle>
          </RoutePermissionGuard>
        </AllowOnlyLoggedInUserOnlyGuard>
        <BlockingPiecesModal />
      </>
    ),
  }),
  ...ProjectRouterWrapper({
    path: '/flow-import-redirect/:flowId',
    element: (
      <AllowOnlyLoggedInUserOnlyGuard>
        <AfterImportFlowRedirect></AfterImportFlowRedirect>
      </AllowOnlyLoggedInUserOnlyGuard>
    ),
  }),
  {
    path: '/forms/:tenantId/:flowId',
    element: (
      <PageTitle title="Forms">
        <FormPage />
      </PageTitle>
    ),
  },
  {
    path: '/chats/:tenantId/:flowId',
    element: (
      <PageTitle title="Chats">
        <ChatPage />
      </PageTitle>
    ),
  },
  ...ProjectRouterWrapper({
    path: '/runs/:runId',
    element: (
      <AllowOnlyLoggedInUserOnlyGuard>
        <RoutePermissionGuard permission={Permission.READ_RUN}>
          <PageTitle title="Flow Run">
            <FlowRunPage />
          </PageTitle>
        </RoutePermissionGuard>
      </AllowOnlyLoggedInUserOnlyGuard>
    ),
  }),
  {
    path: '/templates/:templateId',
    element: (
      <AllowOnlyLoggedInUserOnlyGuard>
        <PageTitle title="Share Template">
          <ShareTemplatePage />
        </PageTitle>
      </AllowOnlyLoggedInUserOnlyGuard>
    ),
  },
  ...ProjectRouterWrapper({
    path: '/releases/:releaseId',
    element: (
      <DashboardContainer>
        <PageTitle title="Releases">
          <ViewRelease />
        </PageTitle>
      </DashboardContainer>
    ),
  }),
  ...ProjectRouterWrapper({
    path: '/runs',
    element: (
      <DashboardContainer>
        <RoutePermissionGuard permission={Permission.READ_RUN}>
          <PageTitle title="Runs">
            <FlowRunsPage />
          </PageTitle>
        </RoutePermissionGuard>
      </DashboardContainer>
    ),
  }),
  ...ProjectRouterWrapper({
    path: '/tables',
    element: (
      <DashboardContainer>
        <RoutePermissionGuard permission={Permission.READ_TABLE}>
          <PageTitle title="Tables">
            <ApTablesPage />
          </PageTitle>
        </RoutePermissionGuard>
      </DashboardContainer>
    ),
  }),
  ...ProjectRouterWrapper({
    path: '/tables/:tableId',
    element: (
      <RoutePermissionGuard permission={Permission.READ_TABLE}>
        <DashboardContainer removeGutters removeBottomPadding>
          <PageTitle title="Table">
            <ApTableStateProvider>
              <ApTableEditorPage />
            </ApTableStateProvider>
          </PageTitle>
        </DashboardContainer>
      </RoutePermissionGuard>
    ),
  }),
  ...ProjectRouterWrapper({
    path: '/issues',
    element: (
      <DashboardContainer>
        <RoutePermissionGuard permission={Permission.READ_ISSUES}>
          <PageTitle title="Issues">
            <IssuesPage />
          </PageTitle>
        </RoutePermissionGuard>
      </DashboardContainer>
    ),
  }),
  ...ProjectRouterWrapper({
    path: '/connections',
    element: (
      <DashboardContainer>
        <RoutePermissionGuard permission={Permission.READ_APP_CONNECTION}>
          <PageTitle title="Connections">
            <AppConnectionsPage />
          </PageTitle>
        </RoutePermissionGuard>
      </DashboardContainer>
    ),
  }),
  ...ProjectRouterWrapper({
    path: '/releases',
    element: (
      <DashboardContainer>
        <PageTitle title="Releases">
          <ProjectReleasesPage />
        </PageTitle>
      </DashboardContainer>
    ),
  }),
  ...ProjectRouterWrapper({
    path: '/plans',
    element: (
      <FlagRouteGuard flag={ApFlagId.SHOW_BILLING}>
        <DashboardContainer>
          <PageTitle title="Plans">
            <PlansPage />
          </PageTitle>
        </DashboardContainer>
      </FlagRouteGuard>
    ),
  }),
  ...ProjectRouterWrapper({
    path: '/settings',
    element: (
      <DashboardContainer>
        <SettingsRerouter></SettingsRerouter>
      </DashboardContainer>
    ),
  }),
  {
    path: '/forget-password',
    element: (
      <PageTitle title="Forget Password">
        <ResetPasswordPage />
      </PageTitle>
    ),
  },
  {
    path: '/reset-password',
    element: (
      <PageTitle title="Reset Password">
        <ChangePasswordPage />
      </PageTitle>
    ),
  },
  {
    path: '/sign-in',
    element: (
      <EmbedRedirectGuard>
        <PageTitle title="Sign In">
          <SignInPage />
        </PageTitle>
      </EmbedRedirectGuard>
    ),
  },
  {
    path: '/verify-email',
    element: (
      <PageTitle title="Verify Email">
        <VerifyEmail />
      </PageTitle>
    ),
  },
  {
    path: '/sign-up',
    element: (
      <PageTitle title="Sign Up">
        <SignUpPage />
      </PageTitle>
    ),
  },
  ...ProjectRouterWrapper({
    path: '/settings/alerts',
    element: (
      <DashboardContainer>
        <RoutePermissionGuard permission={Permission.READ_ALERT}>
          <ProjectSettingsLayout>
            <PageTitle title="Alerts">
              <AlertsPage />
            </PageTitle>
          </ProjectSettingsLayout>
        </RoutePermissionGuard>
      </DashboardContainer>
    ),
  }),
  ...ProjectRouterWrapper({
    path: '/settings/appearance',
    element: (
      <DashboardContainer>
        <ProjectSettingsLayout>
          <PageTitle title="Appearance">
            <AppearancePage />
          </PageTitle>
        </ProjectSettingsLayout>
      </DashboardContainer>
    ),
  }),
  ...ProjectRouterWrapper({
    path: '/settings/sessions',
    element: (
      <DashboardContainer>
        <ProjectSettingsLayout>
          <PageTitle title="Sessions">
            <SessionsPage />
          </PageTitle>
        </ProjectSettingsLayout>
      </DashboardContainer>
    ),
  }),
  ...ProjectRouterWrapper({
    path: '/settings/general',
    element: (
      <DashboardContainer>
        <ProjectSettingsLayout>
          <PageTitle title="General">
            <GeneralPage />
          </PageTitle>
        </ProjectSettingsLayout>
      </DashboardContainer>
    ),
  }),
  ...ProjectRouterWrapper({
    path: '/settings/pieces',
    element: (
      <DashboardContainer>
        <ProjectSettingsLayout>
          <PageTitle title="Pieces">
            <ProjectPiecesPage />
          </PageTitle>
        </ProjectSettingsLayout>
      </DashboardContainer>
    ),
  }),
  ...ProjectRouterWrapper({
    path: '/settings/team',
    element: (
      <DashboardContainer>
        <RoutePermissionGuard permission={Permission.READ_PROJECT_MEMBER}>
          <ProjectSettingsLayout>
            <PageTitle title="Team">
              <TeamPage />
            </PageTitle>
          </ProjectSettingsLayout>
        </RoutePermissionGuard>
      </DashboardContainer>
    ),
  }),
  {
    path: '/team',
    element: <Navigate to="/settings/team" replace></Navigate>,
  },

  ...ProjectRouterWrapper({
    path: '/settings/environments',
    element: (
      <DashboardContainer>
        <RoutePermissionGuard permission={Permission.READ_PROJECT_RELEASE}>
          <ProjectSettingsLayout>
            <PageTitle title="Environments">
              <EnvironmentPage />
            </PageTitle>
          </ProjectSettingsLayout>
        </RoutePermissionGuard>
      </DashboardContainer>
    ),
  }),

  ...ProjectRouterWrapper({
    path: '/mcp',
    element: (
      <DashboardContainer>
        <RoutePermissionGuard permission={Permission.READ_MCP}>
          <McpAccessGuard>
            <PageTitle title="MCP">
              <McpServersPage />
            </PageTitle>
          </McpAccessGuard>
        </RoutePermissionGuard>
      </DashboardContainer>
    ),
  }),
  ...ProjectRouterWrapper({
    path: '/mcp/:mcpId',
    element: (
      <DashboardContainer>
        <RoutePermissionGuard permission={Permission.READ_MCP}>
          <McpAccessGuard>
            <PageTitle title="MCP">
              <McpPage />
            </PageTitle>
          </McpAccessGuard>
        </RoutePermissionGuard>
      </DashboardContainer>
    ),
  }),

  {
    path: '/invitation',
    element: (
      <PageTitle title="Accept Invitation">
        <AcceptInvitation />
      </PageTitle>
    ),
  },

  {
    path: '/404',
    element: (
      <PageTitle title="Not Found">
        <NotFoundPage />
      </PageTitle>
    ),
  },
  {
    path: '/platform/projects',
    element: (
      <PlatformAdminContainer>
        <PageTitle title="Projects">
          <ProjectsPage />
        </PageTitle>
      </PlatformAdminContainer>
    ),
  },
  {
    path: '/platform/setup/pieces',
    element: (
      <PlatformAdminContainer>
        <PlatformSecondSidebarLayout type="setup">
          <PageTitle title="Pieces">
            <PlatformPiecesPage />
          </PageTitle>
        </PlatformSecondSidebarLayout>
      </PlatformAdminContainer>
    ),
  },
  {
    path: '/platform/analytics',
    element: (
      <PlatformAdminContainer>
        <PageTitle title="Analytics">
          <div className="flex flex-col gap-4 w-full">
            <PlatformMessages />
            <AnalyticsPage />
          </div>
        </PageTitle>
      </PlatformAdminContainer>
    ),
  },
  {
    path: '/platform',
    element: (
      <PlatformAdminContainer>
        <PageTitle title="Platform">
          <Navigate to="/platform/analytics" />
        </PageTitle>
      </PlatformAdminContainer>
    ),
  },
  {
    path: '/platform/setup/connections',
    element: (
      <PlatformAdminContainer>
        <PlatformSecondSidebarLayout type="setup">
          <PageTitle title="Connections">
            <GlobalConnectionsTable />
          </PageTitle>
        </PlatformSecondSidebarLayout>
      </PlatformAdminContainer>
    ),
  },
  {
    path: '/platform/setup/templates',
    element: (
      <PlatformAdminContainer>
        <PlatformSecondSidebarLayout type="setup">
          <PageTitle title="Templates">
            <TemplatesPage />
          </PageTitle>
        </PlatformSecondSidebarLayout>
      </PlatformAdminContainer>
    ),
  },
  {
    path: '/platform/setup/branding',
    element: (
      <PlatformAdminContainer>
        <PlatformSecondSidebarLayout type="setup">
          <PageTitle title="Branding">
            <BrandingPage />
          </PageTitle>
        </PlatformSecondSidebarLayout>
      </PlatformAdminContainer>
    ),
  },
  {
    path: '/platform/users',
    element: (
      <PlatformAdminContainer>
        <PageTitle title="Users">
          <UsersPage />
        </PageTitle>
      </PlatformAdminContainer>
    ),
  },
  {
    path: '/platform/setup/ai',
    element: (
      <PlatformAdminContainer>
        <PlatformSecondSidebarLayout type="setup">
          <PageTitle title="AI">
            <AIProvidersPage />
          </PageTitle>
        </PlatformSecondSidebarLayout>
      </PlatformAdminContainer>
    ),
  },
  {
    path: '/platform/security/api-keys',
    element: (
      <PlatformAdminContainer>
        <PlatformSecondSidebarLayout type="security">
          <PageTitle title="API Keys">
            <ApiKeysPage />
          </PageTitle>
        </PlatformSecondSidebarLayout>
      </PlatformAdminContainer>
    ),
  },
  {
    path: '/platform/security/audit-logs',
    element: (
      <PlatformAdminContainer>
        <PlatformSecondSidebarLayout type="security">
          <PageTitle title="Audit Logs">
            <AuditLogsPage />
          </PageTitle>
        </PlatformSecondSidebarLayout>
      </PlatformAdminContainer>
    ),
  },
  {
    path: '/platform/infrastructure/workers',
    element: (
      <PlatformAdminContainer>
        <PlatformSecondSidebarLayout type="infrastructure">
          <PageTitle title="Workers">
            <SettingsWorkersPage />
          </PageTitle>
        </PlatformSecondSidebarLayout>
      </PlatformAdminContainer>
    ),
  },
  {
    path: '/platform/infrastructure/health',
    element: (
      <PlatformAdminContainer>
        <PlatformSecondSidebarLayout type="infrastructure">
          <PageTitle title="System Health">
            <SettingsHealthPage />
          </PageTitle>
        </PlatformSecondSidebarLayout>
      </PlatformAdminContainer>
    ),
  },
  {
    path: '/platform/setup/billing',
    element: (
      <PlatformAdminContainer>
        <PageTitle title="Billing">
          <PlatformSecondSidebarLayout type="setup">
            <SettingsBilling />
          </PlatformSecondSidebarLayout>
        </PageTitle>
      </PlatformAdminContainer>
    ),
  },
  {
    path: '/platform/security/signing-keys',
    element: (
      <PlatformAdminContainer>
        <PlatformSecondSidebarLayout type="security">
          <PageTitle title="Signing Keys">
            <SigningKeysPage />
          </PageTitle>
        </PlatformSecondSidebarLayout>
      </PlatformAdminContainer>
    ),
  },
  {
    path: '/platform/security/sso',
    element: (
      <PlatformAdminContainer>
        <PlatformSecondSidebarLayout type="security">
          <PageTitle title="SSO">
            <SSOPage />
          </PageTitle>
        </PlatformSecondSidebarLayout>
      </PlatformAdminContainer>
    ),
  },
  {
    path: '/platform/setup/license-key',
    element: (
      <PlatformAdminContainer>
        <PlatformSecondSidebarLayout type="setup">
          <PageTitle title="License Key">
            <LicenseKeyPage />
          </PageTitle>
        </PlatformSecondSidebarLayout>
      </PlatformAdminContainer>
    ),
  },
  {
    path: '/platform/security/project-roles',
    element: (
      <PlatformAdminContainer>
        <PlatformSecondSidebarLayout type="security">
          <PageTitle title="Project Roles">
            <ProjectRolePage />
          </PageTitle>
        </PlatformSecondSidebarLayout>
      </PlatformAdminContainer>
    ),
  },
  {
    path: '/platform/setup',
    element: (
      <PlatformAdminContainer>
        <PageTitle title="Platform Setup">
          <Navigate to="/platform/setup/ai" replace />
        </PageTitle>
      </PlatformAdminContainer>
    ),
  },
  {
    path: '/platform/infrastructure',
    element: (
      <PlatformAdminContainer>
        <PageTitle title="Platform Infrastructure">
          <Navigate to="/platform/infrastructure/workers" replace />
        </PageTitle>
      </PlatformAdminContainer>
    ),
  },
  {
    path: '/platform/security',
    element: (
      <PlatformAdminContainer>
        <PageTitle title="Platform Security">
          <Navigate to="/platform/security/audit-logs" replace />
        </PageTitle>
      </PlatformAdminContainer>
    ),
  },
  {
    path: INTERNAL_ADMIN_PATH,
    element: (
      <KizeoAdminAuthGuard>
        <AdminContainer>
          <Outlet />
        </AdminContainer>
      </KizeoAdminAuthGuard>
    ),
    children: [
      {
        index: true,
        element: <AdminLandingRedirect />,
      },
      {
        // Full width, no second sidebar: these are landing pages, not entries
        // in a list of tables.
        path: 'health',
        element: (
          <PageTitle title="Santé">
            <AdminHealthPage />
          </PageTitle>
        ),
      },
      {
        path: 'home',
        element: (
          <PageTitle title="Vue d'ensemble">
            <AdminHomePage />
          </PageTitle>
        ),
      },
      {
        path: 'fair-queue',
        element: (
          <PageTitle title="Fair Queue">
            <AdminFairQueuePage />
          </PageTitle>
        ),
      },
      {
        path: 'overview',
        element: (
          <AdminSecondSidebarLayout>
            <Outlet />
          </AdminSecondSidebarLayout>
        ),
        children: [
          {
            index: true,
            element: <Navigate to="clients" replace />,
          },
          {
            path: 'clients',
            element: (
              <PageTitle title="Clients">
                <AdminClientsPage />
              </PageTitle>
            ),
          },
          {
            path: 'users',
            element: (
              <PageTitle title="Utilisateurs">
                <AdminUsersPage />
              </PageTitle>
            ),
          },
          {
            path: 'flows',
            element: (
              <PageTitle title="Flux">
                <AdminFlowsPage />
              </PageTitle>
            ),
          },
          {
            path: 'executions',
            element: (
              <PageTitle title="Exécutions">
                <AdminExecutionsPage />
              </PageTitle>
            ),
          },
          {
            path: 'mcps',
            element: (
              <PageTitle title="MCPs">
                <AdminMcpsPage />
              </PageTitle>
            ),
          },
        ],
      },
      {
        path: 'clients/:clientId',
        element: (
          <PageTitle title="Client">
            <ClientDetailPage />
          </PageTitle>
        ),
        children: [
          {
            index: true,
            element: <Navigate to="users" replace />,
          },
          {
            path: 'users',
            element: <ClientUsersTab />,
          },
          {
            path: 'flows',
            element: <ClientFlowsTab />,
          },
          {
            path: 'executions',
            element: <ClientExecutionsTab />,
          },
          {
            path: 'mcps',
            element: <ClientMcpsTab />,
          },
        ],
      },
      {
        path: 'catalog',
        element: (
          <PageTitle title="Catalogue">
            <AdminCatalogPage />
          </PageTitle>
        ),
      },
      {
        path: 'catalog/:pieceName',
        element: (
          <PageTitle title="Catalogue">
            <AdminCatalogPieceDetailPage />
          </PageTitle>
        ),
      },
      {
        path: 'swaggers',
        element: (
          <PageTitle title="Swaggers">
            <AdminSwaggersPage />
          </PageTitle>
        ),
      },
      {
        path: 'swaggers/:swaggerId',
        element: (
          <PageTitle title="Swagger">
            <AdminSwaggerDetailPage />
          </PageTitle>
        ),
      },
      {
        path: 'mcps/:tenantId/:mcpId',
        element: (
          <PageTitle title="MCP">
            <AdminMcpDetailPage />
          </PageTitle>
        ),
      },
      {
        path: 'users/:userId',
        element: (
          <PageTitle title="Utilisateur">
            <UserDetailPage />
          </PageTitle>
        ),
        children: [
          {
            index: true,
            element: <Navigate to="flows" replace />,
          },
          {
            path: 'flows',
            element: <UserFlowsTab />,
          },
          {
            path: 'executions',
            element: <UserExecutionsTab />,
          },
          {
            path: 'mcps',
            element: <UserMcpsTab />,
          },
        ],
      },
    ],
  },
  {
    path: '/internal-admin/executions/:tenantId/:runId',
    element: (
      <KizeoAdminAuthGuard>
        <PageTitle title="Execution Viewer">
          <AdminExecutionViewerPage />
        </PageTitle>
      </KizeoAdminAuthGuard>
    ),
  },
  {
    path: '/internal-admin/flows/:tenantId/:flowId',
    element: (
      <KizeoAdminAuthGuard>
        <PageTitle title="Flow Viewer">
          <AdminFlowViewerPage />
        </PageTitle>
      </KizeoAdminAuthGuard>
    ),
  },
  {
    path: '/redirect',
    element: <RedirectPage></RedirectPage>,
  },
  {
    path: '/*',
    element: (
      <PageTitle title="Redirect">
        <DefaultRoute></DefaultRoute>
      </PageTitle>
    ),
  },
];
const ApRouter = () => {
  const { embedState } = useEmbedding();

  const router = useMemo(() => {
    const activeRoutes = routes.filter(
      (r) => !r.path || !isRouteBlocked(r.path),
    );
    return embedState.isEmbedded
      ? createMemoryRouter(activeRoutes, {
          initialEntries: [window.location.pathname],
        })
      : createBrowserRouter(activeRoutes);
  }, [embedState.isEmbedded]);

  useEffect(() => {
    if (!embedState.isEmbedded) {
      return;
    }

    const handleVendorRouteChange = (
      event: MessageEvent<ActivepiecesVendorRouteChanged>,
    ) => {
      if (
        event.source === window.parent &&
        event.data.type === ActivepiecesVendorEventName.VENDOR_ROUTE_CHANGED
      ) {
        const targetRoute = event.data.data.vendorRoute;
        router.navigate(targetRoute);
      }
    };

    window.addEventListener('message', handleVendorRouteChange);

    return () => {
      window.removeEventListener('message', handleVendorRouteChange);
    };
  }, [embedState.isEmbedded, router.navigate]);

  const getParentRoute = (khubRoute: string) => {
    if (khubRoute.includes('/flows')) return '/khub/flows';
    if (khubRoute.includes('/runs')) return '/khub/runs';
    if (khubRoute.includes('/connections')) return '/khub/connections';
    if (khubRoute.includes('/mcp')) return '/khub/mcp';
    return null;
  };

  useEffect(() => {
    if (!embedState.isEmbedded) {
      return;
    }
    router.subscribe((state) => {
      window.parent.postMessage(
        {
          type: ActivepiecesClientEventName.CLIENT_ROUTE_CHANGED,
          data: {
            route: state.location.pathname,
            parentRoute: getParentRoute(state.location.pathname),
          },
        },
        '*',
      );
    });
  }, [router, embedState.isEmbedded]);

  return <RouterProvider router={router}></RouterProvider>;
};

export { ApRouter };
